// Bulk stock allocation import — paste from a spreadsheet or pick a CSV.
//
// Same shape as the customer importer, and the same rule: nothing is written
// until the operator has seen, row by row, what will happen. Stock is the one
// thing in this app that a wrong import corrupts quietly — an oversized qty
// looks fine until the event runs out mid-sale.

import { useMemo, useRef, useState } from 'react';
import { Download, Upload, ClipboardPaste, AlertTriangle, Check, RefreshCw } from 'lucide-react';
import { downloadStockTemplate } from '@/lib/importTemplates';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { Chip } from '@/components/ui/Chip';
import { parseAllocationRows, type ParsedAllocationRow } from '@/lib/importParse';
import { useProducts } from '@/hooks/useProducts';
import { useEvent } from '@/hooks/useEvents';
import { useEventAllocations, useSetEventAllocation } from '@/hooks/useEventAllocations';
import { formatDate, formatNumber, isoDateToMs } from '@/lib/format';
import type { EventStockAllocation, Product } from '@/types/domain';
import { IMPORT_ACCEPT, spreadsheetFileToText } from '@/lib/spreadsheet';

export interface AllocationImportDialogProps {
  open: boolean;
  eventId: string;
  onClose: () => void;
}

type RowStatus = 'baru' | 'perbarui' | 'sama' | 'invalid';

interface PreviewRow extends ParsedAllocationRow {
  status: RowStatus;
  reason?: string;
  product?: Product;
  existing?: EventStockAllocation;
}

/** Mirrors the unique index: a null expiry has to match another null expiry. */
const batchKey = (productId: string, lot: string, expiry?: string) =>
  `${productId}|${lot}|${expiry ?? ''}`;

export function AllocationImportDialog({ open, eventId, onClose }: AllocationImportDialogProps) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ ok: number; failed: { line: number; reason: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: products } = useProducts({ isActive: true });
  const { data: allocations } = useEventAllocations(eventId);
  const { data: event } = useEvent(eventId);
  const setAllocation = useSetEventAllocation();

  const parsed = useMemo(() => parseAllocationRows(text), [text]);

  const bySku = useMemo(() => {
    const m = new Map<string, Product>();
    // Case-insensitive: a warehouse spreadsheet will not match the catalogue's
    // capitalisation, and rejecting "prd-001" against "PRD-001" would be
    // technically correct and completely useless.
    for (const p of products ?? []) m.set(p.sku.trim().toLowerCase(), p);
    return m;
  }, [products]);

  const byBatch = useMemo(() => {
    const m = new Map<string, EventStockAllocation>();
    for (const a of allocations ?? []) {
      const exp = a.expiryDate ? new Date(a.expiryDate).toISOString().slice(0, 10) : undefined;
      m.set(batchKey(a.productId, a.lotNumber ?? '', exp), a);
    }
    return m;
  }, [allocations]);

  const preview: PreviewRow[] = useMemo(
    () =>
      parsed.rows.map((r) => {
        const product = bySku.get(r.sku.toLowerCase());
        if (!product) return { ...r, status: 'invalid', reason: `SKU "${r.sku}" tidak ada di katalog` };
        if (!Number.isFinite(r.qty) || r.qty < 0) {
          return { ...r, status: 'invalid', reason: `Qty "${r.qtyRaw}" tidak valid`, product };
        }
        // Refused rather than treated as "no expiry": the expiry is part of the
        // batch identity, so a date we cannot read would quietly create a second
        // batch of the same lot instead of updating the existing one.
        if (r.expiryUnreadable) {
          return {
            ...r, product, status: 'invalid',
            reason: 'Tanggal tidak dikenali — pakai 12-Nov-2030 (nama bulan), 2030-11-12, atau 12/11/2030',
          };
        }
        const existing = byBatch.get(batchKey(product.id, r.lotNumber ?? '', r.expiryDate));
        if (existing) {
          // The database refuses a qty below what has already sold; saying so
          // here means the operator fixes the sheet instead of meeting an error
          // halfway through the import.
          if (r.qty < existing.qtySold) {
            return {
              ...r, product, existing, status: 'invalid',
              reason: `Qty ${r.qty} di bawah yang sudah terjual (${existing.qtySold})`,
            };
          }
          // A row from the prefilled template the operator did not touch:
          // same qty and nothing else given differently. Skipped, so the
          // preview shows only what will actually change.
          const unchanged =
            r.qty === existing.qtyIn &&
            (!r.unit || r.unit === existing.unit) &&
            (!r.orderForm || r.orderForm === existing.orderForm);
          if (unchanged) return { ...r, product, existing, status: 'sama' };
          return { ...r, product, existing, status: 'perbarui' };
        }
        return { ...r, product, status: 'baru' };
      }),
    [parsed.rows, bySku, byBatch],
  );

  const counts = useMemo(() => ({
    baru: preview.filter((r) => r.status === 'baru').length,
    perbarui: preview.filter((r) => r.status === 'perbarui').length,
    sama: preview.filter((r) => r.status === 'sama').length,
    invalid: preview.filter((r) => r.status === 'invalid').length,
  }), [preview]);

  const reset = () => {
    setText(''); setFileName(null); setDone(null); setError(null); setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try { setText(await spreadsheetFileToText(f)); setFileName(f.name); }
    catch (e) { setError(e instanceof Error ? e.message : 'File tidak bisa dibaca.'); }
  };

  const handleImport = async () => {
    const rows = preview.filter((r) => r.status === 'baru' || r.status === 'perbarui');
    if (rows.length === 0) return;
    setBusy(true);
    setError(null);
    const failed: { line: number; reason: string }[] = [];
    let ok = 0;
    // Sequential on purpose. set_event_allocation takes a row lock per batch,
    // and firing a hundred in parallel would serialise in the database anyway
    // while making it impossible to say which row failed.
    for (const r of rows) {
      try {
        await setAllocation.mutateAsync({
          eventId,
          productId: r.product!.id,
          qtyIn: r.qty,
          lotNumber: r.lotNumber,
          expiryDate: r.expiryDate ? isoDateToMs(r.expiryDate) : undefined,
          unit: r.unit,
          orderForm: r.orderForm,
          reason: `Import stok${fileName ? ` — ${fileName}` : ''}`,
        });
        ok += 1;
      } catch (e) {
        failed.push({ line: r.line, reason: e instanceof Error ? e.message : 'Gagal' });
      }
    }
    setBusy(false);
    setDone({ ok, failed });
  };

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import stok dari Excel"
      description="Tempel dari Excel, atau pilih file .xlsx / CSV. Urutan kolom: SKU, Qty, Lot number, Expired date, Satuan, Form order. Tanggal: 12-Nov-2030."
      size="xl"
      footer={
        done ? (
          <Button variant="primary" onClick={() => { reset(); onClose(); }}>Selesai</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>Batal</Button>
            <Button
              variant="primary"
              onClick={handleImport}
              loading={busy}
              disabled={counts.baru + counts.perbarui === 0}
            >
              {counts.baru + counts.perbarui > 0
                ? `Import ${formatNumber(counts.baru + counts.perbarui)} baris`
                : 'Import'}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="space-y-3">
          <div className="p-4 rounded border border-success/30 bg-success/5">
            <p className="text-body font-medium text-ink">{formatNumber(done.ok)} baris stok tersimpan.</p>
          </div>
          {done.failed.length > 0 && (
            <div className="p-4 rounded border border-danger/30 bg-danger/5">
              <p className="text-small font-medium text-danger mb-2">
                {formatNumber(done.failed.length)} baris gagal:
              </p>
              <ul className="text-caption text-ink-subtle space-y-1 max-h-40 overflow-y-auto">
                {done.failed.map((f, i) => <li key={i}>Baris {f.line} — {f.reason}</li>)}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              ref={fileRef}
              type="file"
              accept={IMPORT_ACCEPT}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button variant="secondary" leftIcon={<Upload size={16} />} onClick={() => fileRef.current?.click()}>
              Pilih file Excel / CSV
            </Button>
            {/* The template carries the catalogue: a SKU dropdown fed by a
                "Daftar produk" sheet, and a column that names the product for
                the SKU chosen — nobody has to know SKUs by heart. */}
            <Button
              variant="ghost"
              leftIcon={<Download size={16} />}
              disabled={!products || !allocations}
              onClick={() => {
                setError(null);
                void downloadStockTemplate(products ?? [], {
                  allocations: allocations ?? [],
                  eventCode: event?.code,
                }).catch((e) =>
                  setError(e instanceof Error ? e.message : 'Gagal membuat template'),
                );
              }}
            >
              {(allocations?.length ?? 0) > 0
                ? 'Download stok event (Excel) untuk diubah'
                : 'Download template Excel (dengan daftar SKU)'}
            </Button>
            {fileName && <Chip variant="default">{fileName}</Chip>}
            {text && <Button variant="ghost" onClick={reset}>Bersihkan</Button>}
          </div>

          <div>
            <label htmlFor="alloc-paste" className="block text-small font-medium text-ink mb-1.5">
              <ClipboardPaste size={14} className="inline mr-1.5 -mt-0.5" />
              Atau tempel langsung dari Excel
            </label>
            <textarea
              id="alloc-paste"
              value={text}
              onChange={(e) => { setText(e.target.value); setFileName(null); }}
              rows={5}
              spellCheck={false}
              className="w-full rounded border border-line bg-surface px-3 py-2 text-small font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder={'PRD-001\t50\tLOT-2026-04\t31-Dec-2027\tbox\tFO-2026-0012'}
            />
            <p className="mt-1.5 text-caption text-ink-subtle">
              Produk dicocokkan lewat <b>SKU</b>. Tanggal paling aman pakai nama bulan:{' '}
              <code>12-Nov-2030</code> (atau <code>12-Nov-30</code>). Boleh juga{' '}
              <code>2030-11-12</code> dan <code>12/11/2030</code> — tapi format bulan-dulu
              (11/12/2030) ditolak karena ambigu.
              Kosongkan lot/tanggal/form order kalau tidak ada.
            </p>
          </div>

          {error && (
            <div role="alert" className="p-3 rounded border border-danger/30 bg-danger/10 text-small text-danger">
              {error}
            </div>
          )}

          {preview.length > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Chip variant="success"><Check size={12} className="inline mr-1" />{formatNumber(counts.baru)} lot baru</Chip>
                {counts.perbarui > 0 && (
                  <Chip variant="info"><RefreshCw size={12} className="inline mr-1" />{formatNumber(counts.perbarui)} qty diperbarui</Chip>
                )}
                {counts.sama > 0 && (
                  <Chip variant="default">{formatNumber(counts.sama)} tidak berubah (dilewati)</Chip>
                )}
                {counts.invalid > 0 && (
                  <Chip variant="danger"><AlertTriangle size={12} className="inline mr-1" />{formatNumber(counts.invalid)} tidak valid</Chip>
                )}
                {parsed.headerSkipped && <span className="text-caption text-ink-subtle">Baris judul dilewati.</span>}
              </div>

              <div className="max-h-72 overflow-auto border border-line rounded">
                <Table>
                  <THead>
                    <TR>
                      <TH>Status</TH>
                      <TH>Produk</TH>
                      <TH>Lot</TH>
                      <TH>Expired</TH>
                      <TH className="text-right">Qty</TH>
                      <TH>Satuan</TH>
                      <TH>Form order</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {preview.map((r) => (
                      <TR key={r.line}>
                        <TD>
                          {r.status === 'baru' && <Chip variant="success">Baru</Chip>}
                          {r.status === 'perbarui' && <Chip variant="info">Update</Chip>}
                          {r.status === 'sama' && <span className="text-caption text-ink-subtle">Tidak berubah</span>}
                          {r.status === 'invalid' && (
                            <span className="text-caption text-danger">{r.reason}</span>
                          )}
                        </TD>
                        <TD>
                          <span className="font-mono text-caption">{r.sku}</span>
                          {r.product && (
                            <p className="text-caption text-ink-subtle truncate max-w-[200px]">
                              {r.product.name}
                            </p>
                          )}
                        </TD>
                        <TD className="font-mono text-caption">{r.lotNumber || '—'}</TD>
                        <TD className="text-caption">
                          {r.expiryDate ? formatDate(isoDateToMs(r.expiryDate)) : '—'}
                        </TD>
                        <TD className="text-right tabular-nums">
                          {r.existing && r.status === 'perbarui' ? (
                            <span className="text-ink-subtle">
                              {formatNumber(r.existing.qtyIn)} → <b className="text-ink">{formatNumber(r.qty)}</b>
                            </span>
                          ) : Number.isFinite(r.qty) ? formatNumber(r.qty) : r.qtyRaw}
                        </TD>
                        <TD className="text-caption">{r.unit || r.product?.unit || 'pcs'}</TD>
                        <TD className="font-mono text-caption">{r.orderForm || '—'}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </>
          )}

          {parsed.problems.length > 0 && (
            <div className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-ink-subtle">
              {formatNumber(parsed.problems.length)} baris dilewati karena tidak terbaca (baris{' '}
              {parsed.problems.slice(0, 5).map((p) => p.line).join(', ')}
              {parsed.problems.length > 5 ? '…' : ''}).
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
